/**
 * What may be cached, for how long, and what makes it stale.
 *
 * ⚠️ THIS TABLE IS THE WHOLE SAFETY MODEL. A read with no entry here is NEVER
 * cached — the default is the safe one, so forgetting to add something costs
 * speed, never correctness. `docs/README.frontend-caching-plan.md` §3/§16 forbid
 * "caching everything automatically at interceptor level"; an explicit table is
 * how this stays inside that rule while still reaching the whole app.
 *
 * Tiers are the ones §7 of that document already defines.
 */

export type CacheTier =
  /** 10-30 min: settings, option lists, catalogues — things that barely move. */
  | "reference"
  /** 2-5 min: one record someone reopens. */
  | "detail"
  /** 30-60 s: lists and boards. */
  | "operational"
  /**
   * Money and stock. Served instantly WITH ITS AGE and refreshed behind the
   * scenes; never presented as current, and never the basis of an action that
   * posts money or moves stock offline.
   */
  | "asOf";

export const TIER_TTL_MS: Record<CacheTier, number> = {
  reference: 15 * 60 * 1000,
  detail: 3 * 60 * 1000,
  operational: 45 * 1000,
  // Deliberately short: this exists so a figure APPEARS instantly, not so the
  // app avoids asking. Offline the age is shown and the value still serves.
  asOf: 20 * 1000,
};

export interface CachePolicyEntry {
  /** Matched against the request URL (substring, case-insensitive). */
  match: string;
  tier: CacheTier;
  /** Doctypes whose mutation makes this stale. */
  tags: string[];
}

/**
 * Reads that must NEVER be stored, whatever else matches.
 *
 * Checked before the allow table, so a careless entry above cannot override it.
 * Each line is here for a specific reason, not for tidiness:
 */
const NEVER: Array<{ match: string; why: string }> = [
  // Session and identity. A cached answer keeps a dead session alive, or a
  // revoked role working.
  { match: "/api/method/login", why: "auth" },
  { match: "oauth2.get_token", why: "auth" },
  { match: "auth_api.login", why: "auth" },
  { match: "frappe.auth.get_logged_user", why: "the session probe decides whether we are logged out" },
  { match: "pet_app.api.permissions.", why: "permissions decide what every other read may return" },
  { match: "frappe.core.doctype.user.user.get_roles", why: "permissions" },
  { match: "pet_app.api.branch.get_branch_context", why: "branch scoping feeds the cache key itself" },

  // A stale count makes the pager offer a page that does not exist, and
  // `usePagination.tryNext` then double-fetches to roll back.
  { match: "frappe.client.get_count", why: "pagination correctness" },

  // GETs that WRITE. Caching them is not a staleness problem, it is a silent
  // behaviour change.
  { match: "notifications.test_whatsapp_account", why: "stamps last_health_check_at on the record" },
  { match: "pos.settle_open_invoice", why: "the capability probe poses as a settlement call" },

  // Expensive, parameterised, and used for closing figures.
  { match: "frappe.desk.query_report", why: "financial reporting must be fresh" },

  // Files are streamed, not JSON, and never pass through this layer anyway.
  { match: "/api/method/frappe.utils.file_manager", why: "binary" },
  { match: "download", why: "binary" },
];

/**
 * The allow table. Ordered: the FIRST match wins, so put specific entries above
 * general ones.
 *
 * Stage 2 ships `reference` only; later stages add rows rather than changing the
 * mechanism.
 */
const POLICY: CachePolicyEntry[] = [
  // --- reference: option lists, catalogues, settings -------------------------
  { match: "/api/resource/Item%20Group", tier: "reference", tags: ["Item Group"] },
  { match: "/api/resource/UOM", tier: "reference", tags: ["UOM"] },
  { match: "/api/resource/Customer%20Group", tier: "reference", tags: ["Customer Group"] },
  { match: "/api/resource/Mode%20of%20Payment", tier: "reference", tags: ["Mode of Payment"] },
  { match: "/api/resource/Price%20List", tier: "reference", tags: ["Price List"] },
  { match: "/api/resource/Warehouse", tier: "reference", tags: ["Warehouse"] },
  { match: "/api/resource/Healthcare%20Practitioner", tier: "reference", tags: ["Healthcare Practitioner"] },
  { match: "/api/resource/Disease", tier: "reference", tags: ["Disease"] },
  { match: "/api/resource/Medication", tier: "reference", tags: ["Medication"] },
  { match: "case_sheet_templates.list_templates", tier: "reference", tags: ["Case Sheet Template"] },
  { match: "pet_app.api.medication.get_medications", tier: "reference", tags: ["Medication"] },
  { match: "boarding_rates.get_boarding_rate_grid", tier: "reference", tags: ["Pet Boarding Settings"] },

  // --- detail: a record someone opens, closes and opens again -----------------
  //
  // These are the pages people navigate back to constantly, and their contents
  // change on a human timescale — a pet's name and species, a guardian's address.
  // Three minutes is short enough that an edit made elsewhere shows up quickly,
  // and a save to the same doctype clears it immediately regardless.
  { match: "pet_app.api.pet.get_pet", tier: "detail", tags: ["Pet"] },
  { match: "/api/resource/Pet?", tier: "detail", tags: ["Pet"] },
  { match: "/api/resource/Guardian", tier: "detail", tags: ["Guardian"] },
  { match: "pet_app.api.medical_profile.get_pet_episode_history", tier: "detail", tags: ["Pet", "Pet Care Episode"] },

  // --- operational: lists and boards ------------------------------------------
  //
  // 45 seconds. Long enough that flicking between tabs is free, short enough that
  // a colleague's change appears while you are still looking at the screen.
  { match: "/api/resource/Vet%20Case%20Sheet", tier: "operational", tags: ["Vet Case Sheet"] },
  { match: "/api/resource/Appointment", tier: "operational", tags: ["Appointment"] },
  //
  // ⚠️ Item Price, NOT Item stock. The price list moves rarely and the till reads
  // it on every catalogue page; `Bin` quantities are deliberately absent from this
  // whole table and stay live.
  { match: "/api/resource/Item%20Price", tier: "operational", tags: ["Item Price"] },

  // --- asOf: money, shown instantly WITH ITS AGE ------------------------------
  //
  // ⚠️ These are cached so the figure PAINTS, not so the app stops asking. Every
  // consumer shows how old it is, and nothing that posts money acts on it offline.
  // Cleared by any invoice or payment, which is what actually moves a balance.
  {
    match: "pet_app.api.accounting.get_customer_balance",
    tier: "asOf",
    tags: ["Customer", "Sales Invoice", "Payment Entry"],
  },
];

/**
 * ⚠️ DELIBERATELY NOT CACHED, THOUGH THEY LOOK LIKE OBVIOUS CANDIDATES.
 *
 * Kept as a note rather than as code, because the reason is not visible from the
 * endpoint itself and the next person WILL be tempted:
 *
 * - `PetCareService` reads — `HealthcareCoordinatorWorkspace` polls them on a
 *   timer. A poller that hits a cache is a poller that does nothing, so caching
 *   these would silently stop the coordinator board updating. If they are ever
 *   added here, that poll must pass `cache: "bypass"` in the same change.
 * - `Bin`, `Stock Ledger Entry`, warehouse stock — quantities decide whether a
 *   sale is allowed. They belong in the asOf tier with their age on screen, not
 *   silently reused.
 * - `Sales Invoice`, `Payment Entry`, cashier and driver settlement — money.
 *   Same reasoning, and some are read during closing.
 * - `get_visit_workbench` — the doctor's live working record and the target of
 *   every clinical write. Serving a 45-second-old copy while a colleague edits it
 *   is how two people overwrite each other.
 */

/**
 * An explicit decision from one call site.
 *
 * Needed because a URL alone cannot always tell two reads apart: an autocomplete and
 * a list page both hit `/api/resource/Customer` and differ only in their params. The
 * table stays the default; a function that knows better says so.
 */
export interface CacheOverride {
  tier: CacheTier;
  tags?: string[];
}

/**
 * The doctype a resource URL is about, decoded.
 *
 * `Controllers` values arrive already percent-encoded ("Pet%20Boarding"), which is
 * why this decodes rather than splitting on a space.
 */
export function doctypeFromUrl(url: string): string | null {
  const match = /\/api\/resource\/([^/?]+)/i.exec(String(url ?? ""));

  if (!match)
    return null;

  try {
    return decodeURIComponent(match[1]);
  }
  catch {
    return match[1];
  }
}

/**
 * What a METHOD endpoint makes stale.
 *
 * A resource write says what it touched in its own URL (`/api/resource/Customer`),
 * so nothing needs listing. A method endpoint does not: `create_pos_sale` is a
 * dotted path that mentions no doctype while moving an invoice, a payment, stock
 * and a customer balance. This is that missing half.
 *
 * ⚠️ Over-listing is CHEAP and under-listing is a BUG. A tag that clears something
 * which was not really affected costs one refetch; a missing tag shows a cashier
 * yesterday's balance after they took a payment. When unsure, add the tag.
 */
const METHOD_WRITE_TAGS: Array<{ match: string; tags: string[] }> = [
  // POS — one call that creates and submits an invoice, takes money and moves stock.
  { match: "pos.create_pos_sale", tags: ["Sales Invoice", "Payment Entry", "Customer", "Bin", "Item"] },
  { match: "pos.settle_open_invoice", tags: ["Sales Invoice", "Payment Entry", "Customer"] },
  { match: "accounting.cashier.", tags: ["Payment Entry", "Journal Entry", "Account", "Customer"] },

  // Driver orders — every mutation funnels through one namespace.
  { match: "driver_orders.", tags: ["Sales Invoice", "Payment Entry", "Customer", "Bin", "Driver"] },
  { match: "driver_self.", tags: ["Sales Invoice", "Payment Entry", "Customer", "Driver"] },
  { match: "accounting.driver.", tags: ["Payment Entry", "Driver", "Customer"] },
  { match: "delivery_partners.", tags: ["Delivery Partner", "Sales Invoice", "Payment Entry"] },

  // Clinic.
  { match: "healthcare.case_sheet.", tags: ["Vet Case Sheet", "Vet Visit", "Pet"] },
  { match: "visit_workbench.", tags: ["Vet Visit", "Pet", "Sales Invoice", "Medication"] },
  { match: "care_plan.", tags: ["Pet Care Episode", "Vet Visit", "Pet", "Appointment"] },
  { match: "diagnostics.", tags: ["Lab", "Imaging", "Vet Visit"] },
  { match: "pharmacy.", tags: ["Medication", "Bin", "Vet Visit"] },
  { match: "preventive_care.", tags: ["Pet", "Bin", "Medication"] },

  // Boarding.
  { match: "healthcare.boarding.", tags: ["Pet Boarding", "Pet", "Payment Entry", "Sales Invoice", "Customer"] },
  { match: "boarding_rates.", tags: ["Pet Boarding Settings"] },

  // Scheduling and queue.
  { match: "scheduling.", tags: ["Appointment", "Vet Visit"] },
  { match: "queue.", tags: ["Appointment"] },
];

/** Everything a write to this endpoint should clear. */
export function writeTagsFor(url: string): string[] {
  const haystack = String(url ?? "").toLowerCase();
  const tags = new Set<string>();

  const doctype = doctypeFromUrl(url);

  if (doctype)
    tags.add(doctype);

  for (const rule of METHOD_WRITE_TAGS) {
    if (haystack.includes(rule.match.toLowerCase())) {
      for (const tag of rule.tags)
        tags.add(tag);
    }
  }

  return [...tags];
}

export interface CacheDecision {
  cacheable: boolean;
  tier?: CacheTier;
  ttlMs?: number;
  tags?: string[];
  /** Why it was refused, for the debug panel and for tests. */
  reason?: string;
}

const REFUSED_NOT_LISTED: CacheDecision = { cacheable: false, reason: "not-listed" };

/**
 * Decide a single read.
 *
 * Only GET is ever considered: the branch-selection replay and the 401 refresh
 * replay both re-issue a request from inside the response interceptor, and those
 * paths exist for writes.
 */
export function decideCache(url: string, method = "GET", override?: CacheOverride | null): CacheDecision {
  if (String(method).toUpperCase() !== "GET")
    return { cacheable: false, reason: "not-a-read" };

  const haystack = String(url ?? "").toLowerCase();

  if (!haystack)
    return { cacheable: false, reason: "no-url" };

  // ⚠️ THE BAN LIST WINS OVER AN OVERRIDE. A call site asking to cache the session
  // probe or a count is a mistake, and the table is the place that knows it.
  for (const rule of NEVER) {
    if (haystack.includes(rule.match.toLowerCase()))
      return { cacheable: false, reason: `never:${rule.why}` };
  }

  if (override) {
    const doctype = doctypeFromUrl(url);

    return {
      cacheable: true,
      tier: override.tier,
      ttlMs: TIER_TTL_MS[override.tier],
      // A resource read is about its doctype whether the caller says so or not, so
      // a write to that doctype clears it without anyone maintaining a second list.
      tags: [...(override.tags ?? []), ...(doctype ? [doctype] : [])],
    };
  }

  for (const entry of POLICY) {
    if (haystack.includes(entry.match.toLowerCase())) {
      return {
        cacheable: true,
        tier: entry.tier,
        ttlMs: TIER_TTL_MS[entry.tier],
        tags: entry.tags,
      };
    }
  }

  return REFUSED_NOT_LISTED;
}

/** Test seam: the policy rows, so a test can assert coverage without exporting the array. */
export function policySize(): { allow: number; never: number } {
  return { allow: POLICY.length, never: NEVER.length };
}
